import { DEFAULT_LANG, isRTL, Languages } from '../LanguageContext';
import { FormMetadata, FormTableRow } from '../types';

const LANG_CODE_TO_LANGUAGE: Record<string, Languages> = {
  [DEFAULT_LANG]: Languages.HEBREW,
  'en-US': Languages.ENGLISH,
  ar: Languages.ARABIC,
  ru: Languages.RUSSIAN,
};

const LANGUAGE_TO_LANG_CODE = Object.fromEntries(
  Object.entries(LANG_CODE_TO_LANGUAGE).map(([code, lang]) => [lang, code]),
) as Record<Languages, string>;

/** Display order for preview-link language picker (Hebrew first). */
export const PREVIEW_LANGUAGE_ORDER: readonly Languages[] = [
  Languages.HEBREW,
  Languages.ENGLISH,
  Languages.RUSSIAN,
  Languages.ARABIC,
];

export const PREVIEW_LANGUAGE_OPTIONS = PREVIEW_LANGUAGE_ORDER.map((lang) => ({
  lang,
  label: lang,
  code: langCodeForLanguage(lang),
}));

export type PreviewLanguageOption = (typeof PREVIEW_LANGUAGE_OPTIONS)[number];

/** Strip non-table fields so row state stays display-only. */
export function toFormTableRow(form: FormMetadata): FormTableRow {
  return {
    form_id: form.form_id,
    form_name: form.form_name,
    created_by: form.created_by,
    created_at: form.created_at,
    updated_by: form.updated_by,
    updated_at: form.updated_at,
  };
}

/** Index languages by form_id for O(1) lookup when opening the preview modal. */
export function buildFormLanguagesById(forms: FormMetadata[]): Record<string, string[]> {
  const index: Record<string, string[]> = {};
  for (const form of forms) {
    index[form.form_id] = normalizeFormLanguageCodes(form.languages);
  }
  return index;
}

/** Normalize metadata language codes to canonical BCP-47 values. */
export function normalizeFormLanguageCodes(formLanguages: string[] | undefined): string[] {
  if (!formLanguages?.length) {
    return [DEFAULT_LANG];
  }

  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const raw of formLanguages) {
    const lang = languageFromCode(raw);
    const code = langCodeForLanguage(lang);
    if (!seen.has(code)) {
      seen.add(code);
      normalized.push(code);
    }
  }
  return normalized.length ? normalized : [DEFAULT_LANG];
}

/** Preview picker options filtered to languages listed on the form metadata row. */
export function previewLanguageOptionsForForm(
  formLanguages: string[] | undefined,
): PreviewLanguageOption[] {
  const allowedCodes = new Set(normalizeFormLanguageCodes(formLanguages));
  const options = PREVIEW_LANGUAGE_OPTIONS.filter((option) => allowedCodes.has(option.code));
  if (options.length) {
    return options;
  }
  return PREVIEW_LANGUAGE_OPTIONS.filter((option) => option.code === DEFAULT_LANG);
}

/** Default preview language: Hebrew when available, otherwise first allowed option. */
export function defaultPreviewLanguage(formLanguages: string[] | undefined): Languages {
  const options = previewLanguageOptionsForForm(formLanguages);
  return options.find((option) => option.lang === Languages.HEBREW)?.lang ?? options[0].lang;
}

/** Map UI Languages enum to BCP-47 code for backend/API. */
export function langCodeForLanguage(lang: Languages): string {
  return LANGUAGE_TO_LANG_CODE[lang];
}

/** Direction of text typed in a language; the Console chrome stays Hebrew. */
export function contentDirForLang(code: string): 'rtl' | 'ltr' {
  return isRTL(languageFromCode(code)) ? 'rtl' : 'ltr';
}

/** Map API BCP-47 code to UI Languages enum; defaults to Hebrew. */
export function languageFromCode(code: string | null | undefined): Languages {
  if (!code?.trim()) {
    return Languages.HEBREW;
  }

  const cleaned = code.trim().toLowerCase();
  if (cleaned === DEFAULT_LANG || cleaned === 'he' || cleaned === 'heb') {
    return Languages.HEBREW;
  }
  if (cleaned === 'en-us' || cleaned === 'en' || cleaned === 'eng' || cleaned.startsWith('en')) {
    return Languages.ENGLISH;
  }
  if (cleaned === 'ar' || cleaned === 'ara') {
    return Languages.ARABIC;
  }
  if (cleaned === 'ru' || cleaned === 'rus') {
    return Languages.RUSSIAN;
  }
  return Languages.HEBREW;
}
